import { AuthContextType } from "../features/auth/AuthContext";
import { apiWithTimeout } from "../shared/api/timeout";
import {
    apiHost,
    getErrorMessage,
    isApiConnectionFailure,
} from "../shared/api/helpers";
import type { ApiErrorSetter } from "../shared/api/helpers";

export type ProcessLogQueryRange = "all" | "item_name" | "shop_item_id";
export type ProcessLogComparisonSite = "all" | "danawa" | "enuri" | "naver";
export type ProcessLogType = "all" | "item" | "running";

export interface ProcessLogFilter {
    itemId?: string;
    dateStr?: string;
    processType?: ProcessLogType;
    shopId?: string;
    comparisonSite?: ProcessLogComparisonSite;
    query?: string;
    queryRange?: ProcessLogQueryRange;
    page?: number;
}

export interface ProcessLogPagination {
    count: number;
    next: string | null;
    previous: string | null;
}

const getProcessLogParams = (filter: ProcessLogFilter = {}) => ({
    ...(filter.itemId ? { item_id: filter.itemId } : {}),
    ...(filter.dateStr ? { date: filter.dateStr } : {}),
    ...(filter.processType && filter.processType !== "item" ? { process_type: filter.processType } : {}),
    ...(filter.shopId ? { shop_id: filter.shopId } : {}),
    ...(filter.comparisonSite && filter.comparisonSite !== "all"
        ? { comparison_site: filter.comparisonSite }
        : {}),
    ...(filter.query?.trim() ? { q: filter.query.trim() } : {}),
    ...(filter.queryRange ? { q_range: filter.queryRange } : {}),
    ...(filter.page ? { page: filter.page } : {}),
});

//-----------------------------------------------------------------------------
// ProcessLog
//-----------------------------------------------------------------------------
export async function fetchUserProcessLog(opts: {
    auth: AuthContextType,
    itemId?: string,
    dateStr?: string,
    processType?: ProcessLogType,
    shopId?: string,
    comparisonSite?: ProcessLogComparisonSite,
    query?: string,
    queryRange?: ProcessLogQueryRange,
    page?: number,
    setErrorMessage?: ApiErrorSetter,
    setResult?: (arg: any) => void,
    setPagination?: (pagination: ProcessLogPagination) => void,
}) {
    const { auth, setErrorMessage, setResult, setPagination, ...filter } = opts;
    try {
        const response = await apiWithTimeout.quick.get(`${apiHost}/api/users/log/`, {
            params: getProcessLogParams(filter),
        });
        if (response.data && setResult) {
            // Pagination 응답에서 results 추출, 없으면 전체 응답 사용
            const data = response.data.results || response.data;
            setResult(data);
            setPagination?.({
                count: response.data.count ?? data.length,
                next: response.data.next ?? null,
                previous: response.data.previous ?? null,
            });
        }
        setErrorMessage?.("", false);
    } catch (error: any) {
        if (error.response?.status === 401) {
            auth?.logout();
        }
        const message = getErrorMessage(error);
        if (setErrorMessage) {
            setErrorMessage(message, isApiConnectionFailure(error));
        } else {
            console.error(message);
        }
    }
}

export async function downloadUserProcessLog(opts: {
    auth: AuthContextType,
    dateStr: string,
    processType?: ProcessLogType,
    shopId?: string,
    comparisonSite?: ProcessLogComparisonSite,
    query?: string,
    queryRange?: ProcessLogQueryRange,
}) {
    const { auth, ...filter } = opts;
    try {
        return await apiWithTimeout.long.get(`${apiHost}/api/users/log/download/`, {
            params: getProcessLogParams(filter),
            responseType: "blob",
        });
    } catch (error: any) {
        if (error.response?.status === 401) {
            auth?.logout();
        }
        throw error;
    }
}
